import React, { useMemo, useCallback, useState, useEffect } from 'react';
import cn from 'classnames';
import { DropdownOption } from '@wix/da-ds/pkg/Dropdown/types';
import MultiselectField, {
  MultiselectProps,
} from '@wix/da-ds/pkg/Dropdown/multiselect/MultiselectField';
import GallerySuffixIcon from './_partials/GallerySuffixIcon';
import {
  sortGalleryItems,
  getSelectedFolderIds,
  updateSelectedValue,
} from './utils';
import { GallerySelectItem } from './types';

import s from './GalleryMultiselect.scss';

interface Props
  extends Pick<
    MultiselectProps,
    | 'id'
    | 'className'
    | 'label'
    | 'subLabel'
    | 'isLabelHidden'
    | 'withFloatingList'
    | 'placeholder'
    | 'error'
    | 'innerRef'
  > {
  items: GallerySelectItem[];
  shouldChange?: (items: GallerySelectItem[]) => boolean | Promise<boolean>;
  onChange: (items: GallerySelectItem[], targetFolderIds: number[]) => void;
  withSuffixIcons?: boolean;
}

const GalleryMultiselect: React.FC<Props> = ({
  className,
  items,
  shouldChange,
  onChange,
  withSuffixIcons = true,
  ...rest
}) => {
  const [value, setValue] = useState<GallerySelectItem[]>(
    sortGalleryItems(items)
  );

  const handleChange = useCallback(
    async (isSelected: boolean, itemValue: string) => {
      // `shouldChange` callback enables us to inject a confirmation dialog if needed
      const newValue = updateSelectedValue(value, isSelected, itemValue);
      if (!shouldChange || (await shouldChange(newValue))) {
        const targetFolderIds = getSelectedFolderIds(newValue);
        onChange(newValue, targetFolderIds);
        setValue(newValue);
      }
    },
    [value, onChange, shouldChange]
  );

  const multiselectItems: DropdownOption[] = useMemo(
    () =>
      value.map(({ gallery, selected, disabled }) => ({
        label: gallery.name,
        value: `${gallery.folderId}`,
        suffix: withSuffixIcons && <GallerySuffixIcon gallery={gallery} />,
        className: gallery.parentId ? s['subfolder'] : undefined,
        selected,
        disabled,
      })),
    [value, withSuffixIcons]
  );

  useEffect(() => {
    setValue(sortGalleryItems(items));
  }, [items, onChange]);

  return (
    <MultiselectField
      className={cn(s['root'], className)}
      items={multiselectItems}
      onChange={handleChange}
      {...rest}
    />
  );
};
GalleryMultiselect.displayName = 'GalleryMultiselect';

export default GalleryMultiselect;
